/**
 * The page's side of "keep what you have" for dashboard reads
 * (zeecore/src/projections/shared/unchangedRead.ts).
 *
 * zeecore tags each dashboard answer (weak ETag W/"sv1-…"). We keep the last tagged
 * answer per URL and send its tag back. When the figures have not changed
 * the server answers 304 with no body, and callers get the kept answer as an
 * ordinary 200, with `projection.computed_at` moved on to the Computed-At
 * time so "last updated" still updates. Callers need no changes.
 *
 * Kept in memory only, for this tab, and cleared on sign-out. A 304 means the
 * server's own answer for this request hashes to the kept one, so a kept
 * answer is only ever replayed where the server would have sent the same
 * bytes.
 *
 * If the proxy does not expose ETag (an older Caddyfile), no tag is ever
 * read, so If-None-Match is never sent and nothing changes.
 */

const TAG_PREFIX = 'W/"sv1-';
// An orders-daily answer can be a few MB; a dozen covers the screens one person keeps open.
const MAX_KEPT = 12;

/** Set on a kept answer replayed after a 304, for callers that care. */
export const STORED_READ_UNCHANGED_HEADER = 'X-Stored-Unchanged';

export interface KeptRead {
  tag: string;
  body: string;
}

const kept = new Map<string, KeptRead>();

/** The kept answer for `url`, if any: send its tag as If-None-Match. */
export function keptReadFor(url: string): KeptRead | undefined {
  return kept.get(url);
}

/**
 * After a response arrives: on 304, the kept answer (sent with) as a 200;
 * on a tagged 200, keep it; on anything else that replaces the figures,
 * forget the kept copy.
 */
export async function settleStoredRead(url: string, res: Response, sentWith: KeptRead | undefined): Promise<Response> {
  if (res.status === 304 && sentWith) {
    remember(url, sentWith);
    return replayKept(sentWith.body, res.headers);
  }
  if (res.status === 200) {
    const tag = res.headers.get('ETag');
    if (tag?.startsWith(TAG_PREFIX)) {
      try {
        remember(url, { tag, body: await res.clone().text() });
      } catch {
        kept.delete(url);
      }
    } else {
      kept.delete(url);
    }
  } else if (res.status === 202) {
    // "Preparing": the stored window is being rebuilt; the kept copy is for an older one.
    kept.delete(url);
  }
  return res;
}

export function clearStoredReads(): void {
  kept.clear();
}

function remember(url: string, read: KeptRead): void {
  // Re-insert so the Map's order is least recently used first.
  kept.delete(url);
  kept.set(url, read);
  while (kept.size > MAX_KEPT) {
    const oldest = kept.keys().next().value;
    if (oldest === undefined) break;
    kept.delete(oldest);
  }
}

function replayKept(body: string, notModifiedHeaders: Headers): Response {
  const headers = new Headers(notModifiedHeaders);
  headers.set('Content-Type', 'application/json');
  headers.set(STORED_READ_UNCHANGED_HEADER, '1');
  const computedAt = notModifiedHeaders.get('Computed-At');
  let replayed = body;
  if (computedAt) {
    try {
      const parsed = JSON.parse(body) as { projection?: { computed_at?: string } };
      if (parsed && typeof parsed === 'object' && parsed.projection && typeof parsed.projection === 'object') {
        parsed.projection.computed_at = computedAt;
        replayed = JSON.stringify(parsed);
      }
    } catch {
      // Not JSON we can patch: replay as kept.
    }
  }
  return new Response(replayed, { status: 200, statusText: 'OK', headers });
}
